import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { Users, Briefcase, Plus, Trash2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

interface Mentor {
  id: string;
  name: string;
  industry: string;
  capacity: number;
}

interface Mentee {
  id: string;
  name: string;
  industry: string;
}

const DEFAULT_MENTORS: Mentor[] = [
  { id: '1', name: 'Alice Cooper', industry: 'Software Engineering', capacity: 2 },
  { id: '2', name: 'Bob Vance', industry: 'Marketing', capacity: 1 },
  { id: '3', name: 'Charles Xavier', industry: 'Education', capacity: 3 },
  { id: '4', name: 'Diana Prince', industry: 'Law & Governance', capacity: 2 }
];

const DEFAULT_MENTEES: Mentee[] = [
  { id: '101', name: 'Peter Parker', industry: 'Software Engineering' },
  { id: '102', name: 'Gwen Stacy', industry: 'Software Engineering' },
  { id: '103', name: 'Miles Morales', industry: 'Software Engineering' },
  { id: '104', name: 'Clark Kent', industry: 'Law & Governance' },
  { id: '105', name: 'Bruce Wayne', industry: 'Marketing' },
  { id: '106', name: 'Hal Jordan', industry: 'Education' },
  { id: '107', name: 'Barry Allen', industry: 'Healthcare' } // Unmatched initially (no healthcare mentor)
];

export function AlumniMentorshipCapacity() {
  const [mentors, setMentors] = useState<Mentor[]>(DEFAULT_MENTORS);
  const [mentees, setMentees] = useState<Mentee[]>(DEFAULT_MENTEES);

  // Form states
  const [mentorName, setMentorName] = useState('');
  const [mentorIndustry, setMentorIndustry] = useState('Software Engineering');
  const [mentorCapacity, setMentorCapacity] = useState('2');

  const [menteeName, setMenteeName] = useState('');
  const [menteeIndustry, setMenteeIndustry] = useState('Software Engineering');

  const industries = [
    'Software Engineering',
    'Marketing',
    'Education',
    'Law & Governance',
    'Healthcare',
    'Finance & Consulting'
  ];

  const handleAddMentor = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mentorName.trim()) return;
    setMentors(prev => [
      ...prev,
      {
        id: `m_${Date.now()}`,
        name: mentorName.trim(),
        industry: mentorIndustry,
        capacity: Math.max(1, parseInt(mentorCapacity) || 1)
      }
    ]);
    setMentorName('');
  };

  const handleAddMentee = (e: React.FormEvent) => {
    e.preventDefault();
    if (!menteeName.trim()) return;
    setMentees(prev => [
      ...prev,
      {
        id: `me_${Date.now()}`,
        name: menteeName.trim(),
        industry: menteeIndustry
      }
    ]);
    setMenteeName('');
  };

  const loadSampleData = () => {
    setMentors(DEFAULT_MENTORS);
    setMentees(DEFAULT_MENTEES);
  };

  const clearAll = () => {
    setMentors([]);
    setMentees([]);
  };

  // Pairing algorithm (stable matching logic based on capacity constraint)
  const matches = useMemo(() => {
    const pairings: Record<string, string[]> = {}; // mentorId -> menteeNames[]
    const unmatchedMentees: Mentee[] = [];
    
    // Initialize pairings map
    mentors.forEach(m => {
      pairings[m.id] = [];
    });

    const mentorCapacities = { ...mentors.reduce((acc, m) => ({ ...acc, [m.id]: m.capacity }), {} as Record<string, number>) };

    // Pair mentees
    mentees.forEach(mentee => {
      // Look for a mentor in the preferred industry who has remaining capacity
      const matchingMentor = mentors.find(m => m.industry === mentee.industry && (pairings[m.id]?.length || 0) < mentorCapacities[m.id]);

      if (matchingMentor) {
        pairings[matchingMentor.id].push(mentee.name);
      } else {
        // Fallback: find any mentor who has capacity
        const generalMentor = mentors.find(m => (pairings[m.id]?.length || 0) < mentorCapacities[m.id]);
        if (generalMentor) {
          pairings[generalMentor.id].push(`${mentee.name} (General Placement)`);
        } else {
          unmatchedMentees.push(mentee);
        }
      }
    });

    return { pairings, unmatchedMentees };
  }, [mentors, mentees]);

  // Statistics calculations
  const totalCapacity = useMemo(() => mentors.reduce((acc, m) => acc + m.capacity, 0), [mentors]);
  const matchPercentage = useMemo(() => {
    if (mentees.length === 0) return 0;
    const matchedCount = mentees.length - matches.unmatchedMentees.length;
    return Math.round((matchedCount / mentees.length) * 100);
  }, [mentees, matches]);

  return (
    <div className="space-y-8">
      {/* Intro Banner */}
      <div className="bg-[#FFD600] border-4 border-black p-6 shadow-[4px_4px_0px_#000000] text-black">
        <h3 className="font-display font-black text-2xl uppercase tracking-tight mb-2">Mentorship Capacity Modeler</h3>
        <p className="font-bold text-sm max-w-xl">
          Simulate matchmaking ratios between alumni mentors and current student/graduate mentees. Balance your rosters based on capacity guidelines.
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={loadSampleData} variant="secondary" className="bg-black text-white hover:bg-[#0044FF] border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={clearAll} variant="ghost" className="border-2 border-black text-black hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Clear
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Input Forms */}
        <div className="space-y-6 lg:col-span-1">
          {/* Mentor Form */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-lg uppercase tracking-tight flex items-center gap-2">
              <Users className="text-[#0044FF]" size={20} /> Add Mentor
            </h4>
            <form onSubmit={handleAddMentor} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Mentor Name</label>
                <Input value={mentorName} onChange={e => setMentorName(e.target.value)} placeholder="Alice Vance" className="border-2 border-black rounded-none h-10 font-bold" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Industry</label>
                  <select value={mentorIndustry} onChange={e => setMentorIndustry(e.target.value)} className="w-full border-2 border-black rounded-none h-10 px-2 font-bold bg-white dark:bg-black dark:text-white text-xs">
                    {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Max Capacity</label>
                  <Input type="number" value={mentorCapacity} onChange={e => setMentorCapacity(e.target.value)} placeholder="2" className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#0044FF] text-white border-2 border-black rounded-none font-bold uppercase tracking-wider py-2 hover:bg-black">Add Mentor</Button>
            </form>
          </div>

          {/* Mentee Form */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-lg uppercase tracking-tight flex items-center gap-2">
              <Briefcase className="text-[#FFD600]" size={20} /> Add Mentee
            </h4>
            <form onSubmit={handleAddMentee} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Mentee Name</label>
                <Input value={menteeName} onChange={e => setMenteeName(e.target.value)} placeholder="Peter Parker" className="border-2 border-black rounded-none h-10 font-bold" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Target Industry</label>
                <select value={menteeIndustry} onChange={e => setMenteeIndustry(e.target.value)} className="w-full border-2 border-black rounded-none h-10 px-2 font-bold bg-white dark:bg-black dark:text-white text-xs">
                  {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                </select>
              </div>
              <Button type="submit" className="w-full bg-black text-white border-2 border-black rounded-none font-bold uppercase tracking-wider py-2 hover:bg-[#FFD600] hover:text-black">Add Mentee</Button>
            </form>
          </div>
        </div>

        {/* Dashboard / Pairings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] font-mono font-bold uppercase block text-gray-500">Mentors / Capacity</span>
              <span className="text-3xl font-black font-display text-black dark:text-white">{mentors.length} / {totalCapacity}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] font-mono font-bold uppercase block text-gray-500">Mentees Count</span>
              <span className="text-3xl font-black font-display text-black dark:text-white">{mentees.length}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] font-mono font-bold uppercase block text-gray-500">Match Efficiency</span>
              <span className="text-3xl font-black font-display text-[#0044FF]">{matchPercentage}%</span>
            </div>
          </div>

          {/* Matches Output List */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-xl uppercase tracking-tight">Active Assignments</h4>
            
            {mentors.length === 0 ? (
              <p className="text-sm font-mono text-gray-500 text-center py-8 border-2 border-dashed border-gray-300 uppercase">No active matches. Add mentors to start simulation.</p>
            ) : (
              <div className="space-y-4">
                {mentors.map(mentor => {
                  const paired = matches.pairings[mentor.id] || [];
                  return (
                    <div key={mentor.id} className="border-2 border-black p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#F5F5F0] dark:bg-slate-900">
                      <div>
                        <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-white bg-black dark:bg-white dark:text-black px-1.5 py-0.5 mr-2">{mentor.industry}</span>
                        <h5 className="font-display text-lg font-black uppercase text-black dark:text-white mt-1.5">{mentor.name}</h5>
                        <p className="text-[10px] font-mono text-gray-500 mt-1 uppercase">Capacity Utilized: {paired.length} / {mentor.capacity}</p>
                      </div>
                      
                      {/* Paired List */}
                      <div className="flex-1 max-w-sm sm:text-right">
                        {paired.length === 0 ? (
                          <span className="text-xs text-gray-400 font-mono italic">No Mentees Assigned</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 justify-start sm:justify-end">
                            {paired.map((mName, idx) => (
                              <span key={idx} className="bg-white border border-black dark:bg-black dark:border-white text-[10px] font-bold px-2 py-1 flex items-center gap-1.5 rounded-none uppercase font-mono">
                                <CheckCircle2 size={10} className="text-green-600" /> {mName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <button onClick={() => setMentors(prev => prev.filter(m => m.id !== mentor.id))} className="text-red-500 hover:text-red-700 p-1 border border-transparent hover:border-black shrink-0 self-end sm:self-auto">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Unmatched list */}
            {matches.unmatchedMentees.length > 0 && (
              <div className="border-4 border-red-600 p-4 space-y-2 mt-6 bg-red-50 dark:bg-red-950/20">
                <h5 className="font-display font-black text-sm text-red-600 uppercase tracking-wide flex items-center gap-2">
                  <AlertTriangle size={16} /> Unmatched Mentees ({matches.unmatchedMentees.length})
                </h5>
                <div className="flex flex-wrap gap-2 pt-1">
                  {matches.unmatchedMentees.map(mentee => (
                    <div key={mentee.id} className="bg-white border border-red-600 text-[10px] font-mono font-bold uppercase tracking-tight px-2 py-1 flex items-center justify-between gap-3 dark:bg-black">
                      <span>{mentee.name} ({mentee.industry})</span>
                      <button onClick={() => setMentees(prev => prev.filter(me => me.id !== mentee.id))} className="text-red-500 hover:text-red-700">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
